import React, { useEffect } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useFeatureValue, useGrowthBook } from '@growthbook/growthbook-react';
import ProductListingPage from '../../components/ProductListingPage/ProductListingPage';
import useHardwareFirstShop from '../../hooks/useHardwareFirstShop';
import analytics from '../../lib/analytics';
import {
  DEFAULT_LANDING_ARM,
  LANDING_ARM_FLAG,
  parsePdpParam,
  resolvePdpDestination,
  resolveSiteDestination,
  sendLandingExposureBeacon,
} from './landingSplit';

/**
 * `/products?pdp=s3` is a paid-ad landing test with three arms: the listing, the shop narrowed to the
 * named trackers, or the tracker's marketing-site page. The growthbook feature's experiment rule picks
 * the arm per visitor, and a redirect happens before the listing mounts so only the listing arm logs
 * a listing page view. Without `pdp=` the listing renders as usual and the feature is never evaluated,
 * so organic traffic is never in the experiment. Visitors the shop would send straight back here
 * (partner, rescue and nano contexts, markets without Mini) are not bucketed either.
 */
export default function ProductsRoute() {
  const { search } = useLocation();
  const shopEnabled = useHardwareFirstShop();
  const devices = parsePdpParam(search);
  return shopEnabled && devices.length > 0 ? (
    <LandingSplit devices={devices} search={search} />
  ) : (
    <ProductListingPage />
  );
}

function LandingSplit({ devices, search }: { devices: string[]; search: string }) {
  const arm = useFeatureValue(LANDING_ARM_FLAG, DEFAULT_LANDING_ARM);
  const siteUrl = arm === 'site' ? resolveSiteDestination(devices, search) : null;
  useLeaveForSite(siteUrl);
  if (arm === 'pdp') {
    return <Redirect to={resolvePdpDestination(devices, search)} />;
  }
  return siteUrl ? null : <ProductListingPage />;
}

// the marketing site is another origin and the navigation cancels segment's in-flight requests, so
// the exposure is beaconed right before the hop; growthbook dedupes on the first exposure per visitor.
// without a beacon, wait a beat for the xhr instead, and go anyway if segment is blocked
const SITE_HOP_AFTER_READY_MS = 400;
const SITE_HOP_FALLBACK_MS = 2000;

function useLeaveForSite(url: string | null) {
  const growthBook = useGrowthBook();
  useEffect(() => {
    if (!url) {
      return;
    }
    let left = false;
    const go = () => {
      if (!left) {
        left = true;
        window.location.assign(url);
      }
    };
    analytics.ready(() => {
      const sent = sendLandingExposureBeacon(growthBook?.evalFeature(LANDING_ARM_FLAG));
      return sent ? go() : setTimeout(go, SITE_HOP_AFTER_READY_MS);
    });
    const fallback = setTimeout(go, SITE_HOP_FALLBACK_MS);
    return () => {
      left = true;
      clearTimeout(fallback);
    };
  }, [url, growthBook]);
}
